import { type ReactNode } from 'react';

import { FormikSingleSelect } from '~/components/Formik/FormikSingleSelect';
import { type SelectProps } from '~/components/Select/Select';
import { type ImgixSupportedExportFormat } from '~/utils/Imgix';

export type ImageFormatFormFieldProps = {
  bottomAdornment?: ReactNode;
  disabled?: boolean;
  options: SelectProps['options'];
  label: string;
  name: string;
  onFocus: (isFocused: boolean) => void;
  rightAdornment?: ReactNode;
  ['data-testid']?: string;
  className?: string;
};

export type ImageSizeRatio = '1x (Original)' | '.75x' | '.5x' | '.25x' | 'Custom';

export type ImageSizeOption = {
  label: ImageSizeRatio;
  value: string;
};

export type ImageFormatOption = {
  label: string;
  value: ImgixSupportedExportFormat;
};

/**
 * @description A few of the valid image extensions that an imgix image url can be created for
 * @see https://docs.imgix.com/apis/rendering/format/fm
 */
export const defaultImageExtOptions: ImageFormatOption[] = [
  {
    label: 'JPG',
    value: 'jpg',
  },
  {
    label: 'PNG',
    value: 'png',
  },
  {
    label: 'WEBP',
    value: 'webp',
  },
];

export const defaultImageSizeOptions: ImageSizeOption[] = [
  {
    label: '1x (Original)',
    value: '1',
  },
  {
    label: '.75x',
    value: '0.75',
  },
  {
    label: '.5x',
    value: '0.5',
  },
  {
    label: '.25x',
    value: '0.25',
  },
  {
    label: 'Custom',
    value: '0',
  },
];

export const ImageFormatField = ({
  bottomAdornment,
  disabled,
  options: imageSizeOptions,
  label,
  name,
  rightAdornment,
  'data-testid': testId,
  onFocus,
  className,
}: ImageFormatFormFieldProps) => {
  return (
    <>
      <div className={className}>
        <FormikSingleSelect
          disabled={disabled}
          data-testid={testId}
          isSearchable={false}
          label={label}
          name={name}
          options={imageSizeOptions}
          size="extra-large"
          onOptionsClose={() => {
            onFocus(false);
          }}
          onOptionsOpen={() => {
            onFocus(true);
          }}
        />
        {rightAdornment}
      </div>
      {bottomAdornment}
    </>
  );
};
